export type BlogPostSection = {
  bullets?: string[];
  paragraphs: string[];
  title: string;
};

export type BlogPost = {
  description: string;
  image: string;
  publishedAt: string;
  publishedAtIso: string;
  readingTime: string;
  sections: BlogPostSection[];
  slug: string;
  title: string;
};

export const blogPosts: BlogPost[] = [
  {
    description:
      'Hidden costs can erase your margin before a car ever reaches your listing. Learn the exact numbers to validate before you buy.',
    image:
      'https://cdn.prod.website-files.com/68638c541700891a4cf9dc7e/68638c541700891a4cf9dc8b_cenejs%CC%8Ci_nakup_avtomobila.jpg',
    publishedAt: 'March 20, 2026',
    publishedAtIso: '2026-03-20',
    readingTime: '6 min read',
    sections: [
      {
        paragraphs: [
          'A deal can look strong on the surface and still underperform once real costs hit your books. The most common issue is that purchase decisions are based on asking price only, while transport, prep, and admin are treated as afterthoughts.',
          'Dealers who protect margin build a full-cost view before they commit. That means every euro is estimated up front and updated the moment new information appears.',
        ],
        title: 'Why good deals still lose money',
      },
      {
        bullets: [
          'Pre-purchase inspection and diagnostic checks',
          'Transport, import, and registration fees',
          'Mechanical prep, detailing, and photo prep',
          'Warranty obligations and post-sale service risk',
          'Financing costs and days-in-stock carrying cost',
        ],
        paragraphs: [
          'When you price with only the headline purchase cost, your expected ROI is inflated. A structured checklist keeps your buy decision grounded in reality.',
        ],
        title: 'Cost categories every buyer should include',
      },
      {
        paragraphs: [
          'Before you lock a purchase, set a target resale range, add every expected cost line, and define your minimum acceptable margin. If the numbers do not hold with conservative assumptions, walk away fast.',
          'This discipline creates consistency across all stock levels, from 5 cars per month to 50 plus.',
        ],
        title: 'A practical pre-buy margin framework',
      },
    ],
    slug: 'hidden-costs-used-car-buying',
    title: 'Hidden costs that destroy profit in used-car buying',
  },
  {
    description:
      'AI is not about replacing your team. It is about removing slow manual work so you can source faster and close stronger.',
    image:
      'https://cdn.prod.website-files.com/68638c541700891a4cf9dc7e/68638c541700891a4cf9dc8d_101863.jpg',
    publishedAt: 'March 22, 2026',
    publishedAtIso: '2026-03-22',
    readingTime: '5 min read',
    sections: [
      {
        paragraphs: [
          'Most dealers already know where the bottlenecks are: manual marketplace scanning, inconsistent listing quality, and weak feedback loops on what actually sells. AI works best when it supports these exact moments.',
        ],
        title: 'AI works when it targets repetitive decisions',
      },
      {
        bullets: [
          'Monitor marketplaces continuously instead of checking manually',
          'Flag listings that match your target margin and buying criteria',
          'Improve listing photos to increase perceived value',
          'Standardize listing quality across your whole inventory',
          'Track performance signals and adapt buying strategy faster',
        ],
        paragraphs: [
          'These are high-impact tasks that compound. Saving 30 minutes per listing and acting one day faster on sourcing can change monthly profit more than small negotiation wins.',
        ],
        title: 'Where AI adds direct dealer value',
      },
      {
        paragraphs: [
          'The goal is not more tools. The goal is one operating system where sourcing, listing quality, and profit tracking work together. That is how AI becomes operational, not experimental.',
        ],
        title: 'From isolated automation to a full workflow',
      },
    ],
    slug: 'how-ai-improves-car-sales',
    title: 'How AI helps dealers source faster and sell better',
  },
  {
    description:
      'Demand is shifting across segments. Dealers who read trends early can turn inventory speed into a serious competitive edge.',
    image:
      'https://cdn.prod.website-files.com/68638c541700891a4cf9dc7e/68638c541700891a4cf9dce2_flota-vozil-za-trgovce-z-avtomobili.jpg',
    publishedAt: 'March 24, 2026',
    publishedAtIso: '2026-03-24',
    readingTime: '6 min read',
    sections: [
      {
        paragraphs: [
          'Inventory strategy in 2026 is less about guesswork and more about matching local demand speed with acquisition discipline. The winners are not simply buying cheaper. They are buying what moves quickly at predictable margins.',
        ],
        title: 'What changed in used-car demand',
      },
      {
        bullets: [
          'Fuel-efficient city cars remain highly liquid in urban markets',
          'Reliable family SUVs continue to command stable resale interest',
          'Well-documented service history is becoming a stronger price driver',
          'Buyers are more price-aware, so overpricing slows stock rapidly',
          'Faster listing quality improvements can reduce days to sale',
        ],
        paragraphs: [
          'These patterns vary by region, but the principle is consistent: stock that sells fast protects cash flow and creates room to reinvest into better opportunities.',
        ],
        title: 'Signals smart dealers track each week',
      },
      {
        paragraphs: [
          'Build your buying plan from velocity and margin, not hype. A smaller set of proven models with consistent turnaround usually outperforms wide, uncertain inventory.',
        ],
        title: 'Turn trend data into buying decisions',
      },
    ],
    slug: 'most-in-demand-used-cars-2026',
    title: 'Most in-demand used cars: trends and opportunities',
  },
];
